import { useMemo } from "react";
import { useT } from "@/lib/i18n";
import { useAllQuizQuestions } from "@/data/quiz";
import DateWheelPicker from "@/components/quiz/stepper/DateWheelPicker";
import ScreenHeading from "./ScreenHeading";
import { FOOTNOTE_GAP, HEADING_GAP } from "../layout";
import { ageFromBirthday } from "../age";
import type { BirthdayScreenDef, ScreenComponentProps } from "../types";

/**
 * Birthday via the iOS-style wheel (month · day · year), with the age echoed
 * back live. The reassurance line answers "why do you need this" before the
 * user has to ask — which is where this question usually leaks.
 */
export default function BirthdayScreen({
  def,
  rt,
}: ScreenComponentProps<BirthdayScreenDef>) {
  const { t } = useT();
  const allQuestions = useAllQuizQuestions();
  const question = useMemo(
    () => allQuestions.find((q) => q.id === (def.questionId ?? "birthday")),
    [allQuestions, def.questionId],
  );

  const value = rt.answers[def.field] as string | undefined;
  const age = useMemo(() => ageFromBirthday(value), [value]);

  return (
    <div className="flex-1 flex flex-col">
      <ScreenHeading title={def.titleKey ? t(def.titleKey) : (question?.question ?? "")} />

      <div className={HEADING_GAP}>
        <DateWheelPicker
          value={value}
          onChange={(iso) => rt.setAnswer(def.field, iso)}
        />
      </div>

      {/* Deliberately unanimated. This readout changes on every turn of the
          wheel directly above it, and any animation here is animation running
          against a live scroll gesture. The version that swapped the value
          through framer-motion cancelled iOS momentum outright, and the box is
          a fixed height either way, so the text just changes in place. */}
      <div
        className="mt-4 h-9 flex items-center justify-center"
        aria-live="polite"
      >
        <span className="font-display font-semibold text-[20px] text-neutral-900 dark:text-neutral-50">
          {age != null ? t("onboarding.birthday.ageReadout", { age }) : ""}
        </span>
      </div>

      {def.footnoteKey && (
        <p className={`text-[12px] leading-snug text-neutral-500 dark:text-neutral-400 text-center ${FOOTNOTE_GAP}`}>
          {t(def.footnoteKey)}
        </p>
      )}
    </div>
  );
}
